import { useState } from 'react';
import { PortalData, SectionId } from '../../types/portal';
import { 
  Newspaper, 
  FileText, 
  Phone, 
  Warning, 
  Calendar, 
  Images,
  EnvelopeSimple,
  MapPin,
  Clock,
  Download,
  ArrowRight
} from '@phosphor-icons/react';

interface ClassicTemplateProps {
  data: PortalData;
}

// Sidebar navigation component
const SidebarNav = ({ 
  sections, 
  activeSection, 
  onSectionChange,
  colors 
}: { 
  sections: SectionId[];
  activeSection: SectionId;
  onSectionChange: (section: SectionId) => void;
  colors: PortalData['settings']['colors'];
}) => {
  const sectionConfig: Record<SectionId, { name: string; icon: typeof Newspaper }> = {
    news: { name: 'Nyheter', icon: Newspaper },
    documents: { name: 'Dokument', icon: FileText },
    contact: { name: 'Kontakt', icon: Phone },
    issues: { name: 'Felanmälan', icon: Warning },
    calendar: { name: 'Kalender', icon: Calendar },
    gallery: { name: 'Bildgalleri', icon: Images }
  };

  return (
    <nav className="space-y-1">
      {sections.map(sectionId => {
        const config = sectionConfig[sectionId];
        if (!config) return null;
        const Icon = config.icon;
        const isActive = activeSection === sectionId;
        
        return (
          <button
            key={sectionId}
            onClick={() => onSectionChange(sectionId)}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-left"
            style={{
              backgroundColor: isActive ? `${colors.primary}15` : 'transparent',
              color: isActive ? colors.primary : colors.text,
              borderLeft: isActive ? `3px solid ${colors.primary}` : '3px solid transparent'
            }}
          >
            <Icon size={20} />
            <span className="font-medium">{config.name}</span>
          </button>
        );
      })}
    </nav>
  );
};

// News section
const NewsSection = ({ news, colors }: { news: PortalData['news']; colors: PortalData['settings']['colors'] }) => {
  const [selectedNews, setSelectedNews] = useState<PortalData['news'][0] | null>(null);
  
  if (!news || news.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Newspaper size={48} className="mx-auto mb-4 opacity-50" />
        <p>Inga nyheter publicerade ännu.</p>
      </div>
    );
  }
  
  return (
    <>
      <div className="space-y-4">
        {news.map((item) => (
          <article 
            key={item.id}
            className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className="flex">
              {item.image_url && (
                <div className="w-48 flex-shrink-0">
                  <img 
                    loading="lazy"
                    src={item.image_url} 
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-5 flex-1">
                <time className="text-sm text-slate-400">
                  {new Date(item.published_at).toLocaleDateString('sv-SE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
                <h3 
                  className="text-lg font-semibold mt-1"
                  style={{ color: colors.text }}
                >
                  {item.title}
                </h3>
                <p className="text-slate-600 mt-2 line-clamp-2">
                  {item.content.replace(/<[^>]*>/g, '').substring(0, 200)}...
                </p>
                <button 
                  onClick={() => setSelectedNews(item)}
                  className="mt-3 text-sm font-medium flex items-center gap-1 cursor-pointer hover:gap-2 transition-all"
                  style={{ color: colors.primary }}
                >
                  Läs mer <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* News Modal */}
      {selectedNews && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setSelectedNews(null)}
        >
          <div 
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedNews.image_url && (
              <div className="aspect-video overflow-hidden rounded-t-xl">
                <img 
                  loading="lazy"
                  src={selectedNews.image_url} 
                  alt={selectedNews.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="p-6">
              <time className="text-xs uppercase tracking-wider text-slate-400">
                {new Date(selectedNews.published_at).toLocaleDateString('sv-SE', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
              <h2 
                className="text-2xl font-bold mt-2 mb-4"
                style={{ color: colors.text }}
              >
                {selectedNews.title}
              </h2>
              <div 
                className="prose prose-slate max-w-none"
                style={{ color: colors.text }}
                dangerouslySetInnerHTML={{ __html: selectedNews.content }}
              />
              <button
                onClick={() => setSelectedNews(null)}
                className="mt-6 px-4 py-2 rounded-lg text-sm font-medium"
                style={{ backgroundColor: colors.primary, color: '#fff' }}
              >
                Stäng
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Documents section
const DocumentsSection = ({ documents, colors }: { documents: PortalData['documents']; colors: PortalData['settings']['colors'] }) => {
  if (!documents || documents.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <FileText size={48} className="mx-auto mb-4 opacity-50" />
        <p>Inga dokument tillgängliga.</p>
      </div>
    );
  }

  // Group by category
  const categories = documents.reduce((acc, doc) => {
    const cat = doc.category || 'Övrigt';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(doc);
    return acc;
  }, {} as Record<string, typeof documents>);

  return (
    <div className="space-y-6">
      {Object.entries(categories).map(([category, docs]) => (
        <div key={category}>
          <h3 
            className="font-semibold text-lg mb-3 pb-2 border-b"
            style={{ color: colors.text, borderColor: `${colors.primary}30` }}
          >
            {category}
          </h3>
          <div className="space-y-2">
            {docs.map((doc) => (
              <a 
                key={doc.id}
                href={doc.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-all group"
              >
                <div className="flex items-center gap-3">
                  <FileText size={20} style={{ color: colors.primary }} />
                  <div>
                    <p className="font-medium" style={{ color: colors.text }}>{doc.name}</p>
                    <p className="text-xs text-slate-400">
                      {new Date(doc.uploaded_at).toLocaleDateString('sv-SE')}
                      {doc.file_size && ` • ${(doc.file_size / 1024).toFixed(0)} KB`}
                    </p>
                  </div>
                </div>
                <Download 
                  size={18} 
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: colors.primary }}
                />
              </a>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Contact section
const ContactSection = ({ contact, colors }: { contact: PortalData['contact']; colors: PortalData['settings']['colors'] }) => {
  return (
    <div className="space-y-6">
      {/* Contact info */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="font-semibold text-lg mb-4" style={{ color: colors.text }}>
          Kontaktuppgifter
        </h3>
        <div className="space-y-4">
          {contact.board_email && (
            <a 
              href={`mailto:${contact.board_email}`}
              className="flex items-center gap-3 text-slate-600 hover:text-slate-900"
            >
              <EnvelopeSimple size={20} style={{ color: colors.primary }} />
              {contact.board_email}
            </a>
          )}
          {contact.board_phone && (
            <a 
              href={`tel:${contact.board_phone}`}
              className="flex items-center gap-3 text-slate-600 hover:text-slate-900"
            >
              <Phone size={20} style={{ color: colors.primary }} />
              {contact.board_phone}
            </a>
          )}
          {contact.address && (
            <div className="flex items-center gap-3 text-slate-600">
              <MapPin size={20} style={{ color: colors.primary }} />
              {contact.address}
            </div>
          )}
          {contact.office_hours && (
            <div className="flex items-center gap-3 text-slate-600">
              <Clock size={20} style={{ color: colors.primary }} />
              {contact.office_hours}
            </div>
          )}
        </div>
      </div>

      {/* Board members */}
      {contact.board_members && contact.board_members.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h3 className="font-semibold text-lg mb-4" style={{ color: colors.text }}>
            Styrelse
          </h3>
          <div className="divide-y divide-slate-100">
            {contact.board_members.map((member, idx) => (
              <div key={idx} className="py-3 first:pt-0 last:pb-0">
                <p className="font-medium" style={{ color: colors.text }}>{member.name}</p>
                <p className="text-sm text-slate-500">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Issues section
const IssuesSection = ({ organization, colors }: { organization: PortalData['organization']; colors: PortalData['settings']['colors'] }) => {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 text-center">
      <Warning size={48} style={{ color: colors.primary }} className="mx-auto mb-4" />
      <h3 className="font-semibold text-xl mb-2" style={{ color: colors.text }}>
        Felanmälan
      </h3>
      <p className="text-slate-600 mb-6">
        Har du upptäckt ett fel i fastigheten? Rapportera det enkelt via vårt formulär.
      </p>
      <a 
        href={`/report?org=${organization.slug}`}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-medium"
        style={{ backgroundColor: colors.primary }}
      >
        Anmäl fel
        <ArrowRight size={18} />
      </a>
    </div>
  );
};

// Calendar section
const CalendarSection = ({ calendar, colors }: { calendar: PortalData['calendar']; colors: PortalData['settings']['colors'] }) => {
  if (!calendar || calendar.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Calendar size={48} className="mx-auto mb-4 opacity-50" />
        <p>Inga kommande händelser.</p>
      </div>
    );
  }

  const upcomingEvents = calendar
    .filter(event => new Date(event.start_date) >= new Date())
    .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime());

  return (
    <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
      {upcomingEvents.map((event) => {
        const date = new Date(event.start_date);
        return (
          <div key={event.id} className="flex items-start gap-4 p-4">
            <div 
              className="w-14 h-14 rounded-lg flex flex-col items-center justify-center text-white flex-shrink-0"
              style={{ backgroundColor: colors.primary }}
            >
              <span className="text-xs font-medium opacity-80">
                {date.toLocaleDateString('sv-SE', { month: 'short' }).toUpperCase()}
              </span>
              <span className="text-xl font-bold">{date.getDate()}</span>
            </div>
            <div>
              <h4 className="font-semibold" style={{ color: colors.text }}>{event.title}</h4>
              {event.description && (
                <p className="text-sm text-slate-500 mt-1">{event.description}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                <span>{date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}</span>
                {event.location && (
                  <span className="flex items-center gap-1">
                    <MapPin size={12} /> {event.location}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Gallery section
const GallerySection = ({ gallery, colors }: { gallery: PortalData['gallery']; colors: PortalData['settings']['colors'] }) => {
  if (!gallery || gallery.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Images size={48} className="mx-auto mb-4 opacity-50" />
        <p>Inga bilder i galleriet.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {gallery.map((image) => (
        <div 
          key={image.id}
          className="aspect-square rounded-lg overflow-hidden bg-slate-100 cursor-pointer hover:opacity-90 transition-opacity"
        >
          <img 
            loading="lazy"
            src={image.thumbnail_url || image.url}
            alt={image.caption || ''}
            className="w-full h-full object-cover"
          />
        </div>
      ))}
    </div>
  );
};

export default function ClassicTemplate({ data }: ClassicTemplateProps) {
  const { settings, organization, news, documents, calendar, gallery, contact } = data;
  const { colors, visible_sections } = settings;
  
  // Define preferred section order
  const sectionOrder: SectionId[] = ['news', 'contact', 'calendar', 'issues', 'documents', 'gallery'];
  
  // Filter visible_sections to only include sections with content
  const sectionsWithContent = visible_sections.filter(sectionId => {
    switch (sectionId) {
      case 'news':
        return news && news.length > 0;
      case 'documents':
        return documents && documents.length > 0;
      case 'calendar':
        return calendar && calendar.length > 0;
      case 'gallery':
        return gallery && gallery.length > 0;
      case 'contact':
        return contact?.board_email || contact?.board_phone || contact?.address;
      case 'issues':
        return true;
      default:
        return true;
    }
  }).sort((a, b) => sectionOrder.indexOf(a) - sectionOrder.indexOf(b));
  
  const [activeSection, setActiveSection] = useState<SectionId>(sectionsWithContent[0] || 'news');

  // Section content renderer
  const renderSection = () => {
    switch (activeSection) {
      case 'news':
        return <NewsSection news={data.news} colors={colors} />;
      case 'documents':
        return <DocumentsSection documents={data.documents} colors={colors} />;
      case 'contact':
        return <ContactSection contact={data.contact} colors={colors} />;
      case 'issues':
        return <IssuesSection organization={organization} colors={colors} />;
      case 'calendar':
        return <CalendarSection calendar={data.calendar} colors={colors} />;
      case 'gallery':
        return <GallerySection gallery={data.gallery} colors={colors} />;
      default:
        return null;
    }
  };

  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: colors.background || '#fafafa' }}
    >
      {/* Header */}
      <header 
        className="py-6"
        style={{ backgroundColor: colors.primary }}
      >
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center gap-4">
            {settings.logo_url && (
              <img 
                src={settings.logo_url}
                alt={organization.name}
                className="w-14 h-14 rounded-lg bg-white p-2 object-contain"
              />
            )}
            <div>
              <h1 className="text-2xl font-bold text-white">
                {organization.name}
              </h1>
              {settings.meta_description && (
                <p className="text-white/80 text-sm mt-1">
                  {settings.meta_description}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main content with sidebar */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="w-64 flex-shrink-0 hidden md:block">
            <div className="bg-white border border-slate-200 rounded-lg p-4 sticky top-8">
              <SidebarNav 
                sections={sectionsWithContent}
                activeSection={activeSection}
                onSectionChange={setActiveSection}
                colors={colors}
              />
            </div>
          </aside>

          {/* Mobile navigation */}
          <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-2 z-50">
            <div className="flex justify-around">
              {sectionsWithContent.slice(0, 5).map(sectionId => {
                const icons: Record<SectionId, typeof Newspaper> = {
                  news: Newspaper,
                  documents: FileText,
                  contact: Phone,
                  issues: Warning,
                  calendar: Calendar,
                  gallery: Images
                };
                const Icon = icons[sectionId];
                const isActive = activeSection === sectionId;
                
                return (
                  <button
                    key={sectionId}
                    onClick={() => setActiveSection(sectionId)}
                    className="p-3 rounded-lg"
                    style={{
                      color: isActive ? colors.primary : '#94a3b8'
                    }}
                  >
                    <Icon size={24} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <main className="flex-1 min-w-0 pb-20 md:pb-0">
            {renderSection()}
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer 
        className="py-6 mt-12 border-t"
        style={{ borderColor: `${colors.primary}20` }}
      >
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm text-slate-500">
            {settings.footer_text || `© ${new Date().getFullYear()} ${organization.name}`}
          </p>
        </div>
      </footer>
    </div>
  );
}
